import { useCallback, useEffect, useRef, useState } from "react";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export const useSessionAuth = () => {
  const [user, setUser] = useState(null), [status, setStatus] = useState("checking"), [authError, setAuthError] = useState(null);
  const currentUser = useRef(null), epoch = useRef(0), pending = useRef(null), controller = useRef(null), signingOut = useRef(null), channel = useRef(null);
  const commit = useCallback(value => {
    currentUser.current = value;
    setUser(value);
    setStatus(value ? "authenticated" : "anonymous");
    setAuthError(null);
  }, []);

  const checkAuth = useCallback(({ force = false } = {}) => {
    if (signingOut.current) return Promise.resolve(null);
    if (pending.current && !force) return pending.current;
    controller.current?.abort();
    const revision = ++epoch.current, abort = new AbortController();
    controller.current = abort;
    const timeout = setTimeout(() => abort.abort(), 15000);
    const operation = (async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/auth/me`, { credentials: "include", cache: "no-store", signal: abort.signal });
        if (revision !== epoch.current) return null;
        if (response.status === 401) { commit(null); return null; }
        if (!response.ok) throw new Error("Session check unavailable");
        const value = await response.json();
        if (revision !== epoch.current) return null;
        if (!value?.user_id) throw new Error("Invalid session response");
        commit(value);
        return value;
      } catch {
        if (revision !== epoch.current) return null;
        // A network/server error is not evidence that an authenticated session ended.
        setStatus(currentUser.current ? "authenticated" : "unavailable");
        setAuthError({ action: "check", message: "Your session could not be checked. Please reconnect or retry." });
        return null;
      } finally {
        clearTimeout(timeout);
        if (revision === epoch.current) pending.current = null;
      }
    })();
    pending.current = operation;
    return operation;
  }, [commit]);

  const logout = useCallback(() => {
    if (signingOut.current) return signingOut.current;
    ++epoch.current;
    controller.current?.abort();
    pending.current = null;
    const operation = (async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/auth/logout`, { method: "POST", credentials: "include", signal: AbortSignal.timeout(15000) });
        if (!response.ok) throw new Error("Sign-out failed");
        commit(null);
        channel.current?.postMessage("logout");
        return true;
      } catch {
        setAuthError({ action: "logout", message: "Sign-out could not be completed. Please reconnect and retry signing out." });
        return false;
      } finally { signingOut.current = null; }
    })();
    signingOut.current = operation;
    return operation;
  }, [commit]);

  useEffect(() => {
    const oauthPending = () => window.location.pathname === "/auth/google" || window.location.hash?.includes("session_id=");
    const resume = () => { if (!oauthPending() && document.visibilityState === "visible") checkAuth(); };
    if (!oauthPending()) checkAuth();
    window.addEventListener("focus", resume);
    window.addEventListener("online", resume);
    document.addEventListener("visibilitychange", resume);
    if (typeof BroadcastChannel !== "undefined") {
      channel.current = new BroadcastChannel("dnh-auth");
      channel.current.onmessage = event => {
        if (event.data !== "logout") return;
        ++epoch.current;
        controller.current?.abort();
        pending.current = null;
        commit(null);
      };
    }
    return () => {
      ++epoch.current;
      controller.current?.abort();
      pending.current = null;
      window.removeEventListener("focus", resume);
      window.removeEventListener("online", resume);
      document.removeEventListener("visibilitychange", resume);
      channel.current?.close();
      channel.current = null;
    };
  }, [checkAuth, commit]);

  return { user, authStatus: status, loading: status === "checking" || (status === "unavailable" && !user), authError, checkAuth, refresh: checkAuth, logout };
};